import { Button, Card, Grid, Modal } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { utils as wdUtils } from "washday-sdk"
import CreateEditForm from "../../Customers/CreateEditForm";
import LoaderOverlay from "../../../components/LoaderOverlay/LoaderOverlay";
import { trackEvent } from "../../../utils/monitoring/posthog";
import { MonitoringEventTypes } from "../../../utils/monitoring/Monitoring_Events_Enum";
import { isPossiblePhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { useUpdateCustomerById } from "../../../hooks/customers/mutations";
import { useSelector } from "react-redux";
import { usePostHog } from "posthog-js/react";
import { useForm } from "@mantine/form";
import { emailFormValidator, requiredFieldFormValidator } from "../../../utils/validators/formValidators";
import { showNotification } from "../../../utils/Notification/Notification";
import { ICustomer } from "../../../interfaces/Customer";
import { useLocation } from "react-router-dom";
import { useGetCustomeryId } from "../../../hooks/customers/queries";
import { useContext, useEffect, useMemo } from "react";
import { getFullPhoneNumber } from "../../../utils/strings";
import { OrderContext } from "../../../context/OrderContext";
import { useFormContext } from "react-hook-form";
import { stripLegacyAddressCoordinates } from "../../../utils/customerAddress";
import { getFrequentCustomerCardFormValues, getFrequentCustomerCardPayload } from '../../Customers/frequentCustomerCardForm';
import { hasBackendErrors } from '../../../utils/Notification/BackendErrorsHandler';

const EditCustomerModal = ({ opened, onClose, afterSubmit }: { opened: boolean, onClose: () => void, afterSubmit: (customer: ICustomer) => void }) => {
    const { t } = useTranslation(['NewOrder', 'FieldName', 'CustomersPage', 'FormValidator', 'CommonButtons']);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const searchParamStoreId = searchParams.get('storeId') || '';
    const posthog = usePostHog();
    const { company } = useSelector((state) => state.user);
    const { watch } = useFormContext();
    const customerId = watch('customer');
    const { isLoading: isGetLoading, data: customer } = useGetCustomeryId(customerId, opened && !!customerId);
    const updateById = useUpdateCustomerById(customerId);

    const formValidation = useMemo(() => ({
        name: (value) => {
            const requiredValidatorRes = requiredFieldFormValidator(value);
            if (requiredValidatorRes) return requiredValidatorRes;
        },
        email: (value) => {
            if (value === '') return null;
            const emailValidatorRes = emailFormValidator(value);
            if (emailValidatorRes) return emailValidatorRes;
        },
        fullPhoneNumber: (value, formValues) => {
            try {
                if (!value) return null;
                if (!isPossiblePhoneNumber(value, formValues.phoneCountryName)) {
                    showNotification({ type: 'Error', title: 'Numero telefonico no valido' });
                    return 'Numero telefonico no valido';
                }
            } catch (e) {
                showNotification({ type: 'Error', title: 'Numero telefonico no valido' });
            }
        }
    }), []);

    const initialFormValues = useMemo(() => ({
        name: '',
        frequentCustomerCard: getFrequentCustomerCardFormValues(),
        lastName: '',
        fullPhoneNumber: '',
        phone: '',
        phoneCountryCode: '',
        phoneCountryName: company?.country?.iso,
        email: '',
        address: {
            formattedAddress: '',
            street: '',
            neighborhood: '',
            postalCode: '',
            city: '',
            state: '',
            country: '',
            mapLocation: null
        },
        notes: '',
        privateNotes: '',
        discount: 0,
        credit: 0,
        rfc: '',
        invoiceInfo: {
            legal_name: '',
            tax_system: '',
            zipCode: '',
            applyIsrRetention: false
        },
        isActive: true,
        createdIn: ''
    }), [company?.country?.iso]);

    const form = useForm({
        initialValues: initialFormValues,
        validate: formValidation
    });

    type FormValues = typeof form.values;

    const submit = async (values: FormValues) => {
        try {
            const rawPhoneNumber = values.fullPhoneNumber;
            const selectedCountry = values.phoneCountryName || company?.country?.iso || 'MX';
            const submitValues = {
              ...values,
              frequentCustomerCard: getFrequentCustomerCardPayload(values.frequentCustomerCard),
              email: values.email.trim().toLowerCase()
            };
            if (!submitValues.email) {
                delete submitValues.email;
            }
            const newFormattedFullAddress = wdUtils.getFormattedAddress(values.address);
            if (submitValues.address && newFormattedFullAddress) {
                submitValues.address = {
                    ...submitValues.address,
                    formattedAddress: newFormattedFullAddress
                };
            }
            submitValues.discount = values.discount || 0;
            submitValues.credit = values.credit || 0;
            try {
                if (!rawPhoneNumber) {
                    submitValues.phone = '';
                    submitValues.phoneCountryCode = '';
                    submitValues.phoneCountryName = '';
                } else {
                    const phoneNumber = parsePhoneNumber(rawPhoneNumber, selectedCountry);
                    if (phoneNumber) {
                        submitValues.phoneCountryCode = phoneNumber.countryCallingCode;
                        submitValues.phoneCountryName = phoneNumber.country || '';
                        submitValues.phone = phoneNumber.nationalNumber;
                    } else {
                        console.error('Phone number could not be parsed');
                        form.setFieldValue('phoneCountryCode', '');
                        form.setFieldValue('phoneCountryName', '');
                        submitValues.phoneCountryCode = '';
                        submitValues.phoneCountryName = '';
                        submitValues.phone = '';
                    }
                }
            } catch (error) {
                console.error('Error parsing phone number:', error);
                submitValues.phoneCountryCode = '';
                submitValues.phoneCountryName = '';
                submitValues.phone = '';
            }
            const cleanedSubmitValues = stripLegacyAddressCoordinates(submitValues);
            const res = await updateById.mutateAsync(cleanedSubmitValues);
            trackEvent(posthog, MonitoringEventTypes.POS_PAGE_CUSTOMER_EDIT_SUCCESS, {});
            showNotification({ type: 'Default', title: t('Notifications:success') });
            afterSubmit(res);
        } catch (e) {
            trackEvent(posthog, MonitoringEventTypes.POS_PAGE_CUSTOMER_EDIT_FAIL, {
                error: e,
                data: values
            });
            if (!hasBackendErrors(e)) {
                showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
            }
        }
    };

    useEffect(() => {
        if (customer) {
            const customerObjLoaded = {
                ...customer,
                frequentCustomerCard: getFrequentCustomerCardFormValues(customer.frequentCustomerCard),
                fullPhoneNumber: getFullPhoneNumber(customer.phoneCountryCode, customer.phone, true),
                phoneCountryName: customer.phoneCountryName || company?.country?.iso
            };
            form.setValues(customerObjLoaded);
            form.resetDirty(customerObjLoaded);
        }
    }, [customer]);


    return (
        <Modal opened={opened} onClose={onClose} title={t('NewOrder:editCustomer')}>
            <LoaderOverlay visible={updateById.isPending || isGetLoading} />
            <form onSubmit={form.onSubmit(submit)}>
                <CreateEditForm storeId={searchParamStoreId} form={form} isEditing={true} />
                <Card pos={'sticky'} bottom={5}>
                    <Card.Section inheritPadding py="xs">
                        <Grid align="center">
                            <Grid.Col span={{ xs: 12, md: 6 }}>
                                <Button
                                    fullWidth
                                    color="gray"
                                    loading={updateById.isPending}
                                    disabled={updateById.isPending}
                                    onClick={onClose}>
                                    {t('CommonButtons:cancel')}
                                </Button>
                            </Grid.Col>
                            <Grid.Col span={{ xs: 12, md: 6 }}>
                                <Button fullWidth type="submit" loading={updateById.isPending} >
                                    {t('CommonButtons:submit')}
                                </Button>
                            </Grid.Col>
                        </Grid>
                    </Card.Section>
                </Card>
            </form>
        </Modal>
    );
}

export default EditCustomerModal;
